"use client";

import React, { useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import { HiOutlineXMark, HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import styles from "./photoViewer.module.css"; // You'll need to create this
import { useDialogRouting } from "@/hooks/use-dialog-routing";

interface PhotoViewerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  photos: { url: string; title: string }[];
  currentIndex: number;
  onNavigate: (newIndex: number) => void;
}

export function PhotoViewerDialog({
  isOpen,
  onClose,
  photos,
  currentIndex,
  onNavigate,
}: PhotoViewerDialogProps) {
  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && currentIndex > 0)
        onNavigate(currentIndex - 1);
      if (e.key === "ArrowRight" && currentIndex < photos.length - 1)
        onNavigate(currentIndex + 1);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, photos.length, onClose, onNavigate]);

  useDialogRouting("PhotoViewerDialog", isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // (Escape is now handled by the hook)
      if (e.key === "ArrowLeft" && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      }
      if (e.key === "ArrowRight" && currentIndex < photos.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, photos.length, onNavigate]);

  if (!isOpen || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <button
        className={styles.closeBtn}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
      >
        <HiOutlineXMark size={32} />
      </button>

      <div
        className={styles.viewerContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {currentIndex > 0 && (
          <button
            className={styles.navBtnLeft}
            onClick={() => onNavigate(currentIndex - 1)}
          >
            <HiChevronLeft size={48} />
          </button>
        )}

        <div className={styles.imageWrapper}>
          <Image
            src={currentPhoto.url}
            alt={currentPhoto.title}
            fill
            className={styles.mainImage}
            unoptimized={currentPhoto.url.startsWith("blob:")}
          />
        </div>

        {currentIndex < photos.length - 1 && (
          <button
            className={styles.navBtnRight}
            onClick={() => onNavigate(currentIndex + 1)}
          >
            <HiChevronRight size={48} />
          </button>
        )}
      </div>
      <div className={styles.counter}>
        {currentIndex + 1} / {photos.length}
      </div>
    </div>
  );
}
